import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Task } from "~/api/Api";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import LinksTasksPage, {
  InternalLinkCell,
  StatusBadge,
  formatPercent,
} from "./LinksTasksPage";
import styles from "./NicheLinksPage.module.scss";

const NicheLinksPage = () => {
  usePageTitle(PAGE_TITLES.authorityLinks);
  const { id: siteId } = useParams();

  const columns = useMemo<ColumnDef<Task>[]>(
    () => [
      {
        accessorKey: "status",
        header: "Status",
        enableSorting: false,
        cell: (info) => (
          <StatusBadge status={info.getValue() as string} styles={styles} />
        ),
      },
      {
        accessorKey: "link_title",
        header: "Site",
        cell: (info) => {
          const task = info.row.original;

          return (
            <InternalLinkCell
              to={routes.site.links.nicheTask({
                id: String(siteId),
                taskId: task.id,
              })}
              domain={task.link_domain}
              styles={styles}
            >
              {task.link_title || "No title"}
            </InternalLinkCell>
          );
        },
      },
      {
        accessorKey: "link_domain",
        header: "Domain",
      },
      {
        // Use the API sort key as column id so the sort state sends
        // "keyword_name" to the backend rather than "focus_keyword".
        id: "keyword_name",
        accessorKey: "focus_keyword",
        header: "Focus keyword",
        cell: (info) => (info.getValue() as string) || "ai in seo",
      },
      {
        accessorKey: "probability",
        header: "Relevance",
        cell: (info) => formatPercent(info.getValue() as number | null),
      },
    ],
    [siteId],
  );

  return (
    <LinksTasksPage
      title={PAGE_TITLES.authorityLinks}
      description="Authority opportunities help strengthen your website's relevance and trust through links from established, high-quality websites found via a social graph algorithm."
      siteId={siteId}
      taskType="NicheLink"
      dfyLink={{
        label: "Done-For-You Authority Links",
        to: routes.services.nicheLinks(),
      }}
      searchRansackKey="link_domain_or_link_title_or_keyword_name_i_cont"
      columns={columns}
      styles={styles}
    />
  );
};

export default NicheLinksPage;
